import { ChangeEventHandler, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@bymason-private/auth-provider';
import {
  Alert,
  Button,
  ControllerAutocomplete,
  ControllerMenuItem,
  ControllerModal,
  ControllerSelect,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
  Typography,
} from '@bymason-private/legacy-pattern-components';
import {
  ArtifactDeployment,
  ArtifactType,
  BuildResult,
  BuildStatus,
  GroupModel,
  JobModel,
  useCreateArtifactDeployment,
  useGetGroups,
} from '@bymason/controller-api-hooks';
import { ProjectModel, ProjectWithBuilds, BuildVersion } from '@bymason/controller-api';
import { DEFAULT_GROUPS_QUERY_LIMIT, DEFAULT_GROUP_NAME, DEPLOY_TYPE_MAP } from '../../../app-constants';
import { CreateGroupModal } from '../create-group-modal';

export interface ConfigDeployment extends ArtifactDeployment {
  groupName?: string;
}

interface DeployBuildVersionModalProps {
  project: ProjectModel | Omit<ProjectWithBuilds, 'buildVersions'>;
  buildJobs: (JobModel | BuildVersion)[];
  open: boolean;
  controllerBuildEnabled?: boolean;
  onClose: () => void;
  onSuccess?: (deploy: ConfigDeployment) => void;
  onError?: (message: string, code?: number) => void;
}

export function DeployBuildVersionModal({
  open,
  project,
  buildJobs,
  controllerBuildEnabled,
  onClose,
  onSuccess,
  onError,
}: DeployBuildVersionModalProps) {
  const { authToken = '', customerId } = useAuth();
  const [artifactVersion, setArtifactVersion] = useState<string>('');
  const [targetGroupName, setTargetGroupName] = useState<string | null>(null);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);

  const [getGroups, groups] = useGetGroups(authToken);
  const [createDeploy, deployResult, deployError, deployBusy] = useCreateArtifactDeployment(authToken);

  const handleTargetGroupChange = (evt: unknown, selected: string | null) => {
    setTargetGroupName(selected);
  };

  const handleArtifactVersionChange: ChangeEventHandler<HTMLInputElement> = (evt) => {
    setArtifactVersion(evt.target.value);
  };

  const handleCreateGroupModalOpen = useCallback(() => {
    setShowCreateGroupModal(true);
  }, []);

  const handleCreateGroupModalClose = useCallback(() => {
    setShowCreateGroupModal(false);
  }, []);

  const handleCreateGroupSuccess = (newGroup: GroupModel) => {
    getGroups({ limit: DEFAULT_GROUPS_QUERY_LIMIT });
    setTargetGroupName(newGroup.name);
    setShowCreateGroupModal(false);
  };

  const handleDeployArtifact = useCallback(() => {
    if (customerId && artifactVersion && targetGroupName) {
      createDeploy({
        customer: customerId,
        group: targetGroupName,
        name: project.name,
        version: artifactVersion,
        type: ArtifactType.Config,
        push: true,
      });
    }
  }, [artifactVersion, createDeploy, customerId, targetGroupName, project]);

  useEffect(() => {
    getGroups({ limit: DEFAULT_GROUPS_QUERY_LIMIT });
  }, [getGroups]);

  useEffect(() => {
    if (!deployBusy && !deployError && deployResult) {
      onSuccess && onSuccess({ ...deployResult.data, groupName: targetGroupName || undefined });
      onClose();
    }
  }, [targetGroupName, deployBusy, deployError, deployResult, onClose, onSuccess]);

  useEffect(() => {
    if (onError && deployError) {
      onError(deployError);
    }
  }, [deployError, onError]);

  const filteredGroups = useMemo(() => {
    return groups?.data?.filter((group) => {
      return (
        group.name !== DEFAULT_GROUP_NAME &&
        (!group.deviceFamily || group.deviceFamily === project.deviceFamily) &&
        (!group.apiLevel || group.apiLevel === project.apiLevel)
      );
    });
  }, [project, groups?.data]);

  const filteredBuilds = useMemo(() => {
    return buildJobs.filter((build) => {
      return (
        build.project === project.name &&
        build.status === BuildStatus.Completed &&
        build?.result === BuildResult.Success
      );
    });
  }, [buildJobs, project]);

  const versionOptions = useMemo(() => {
    let verSet: Set<string> = new Set();
    if (filteredBuilds) {
      verSet = filteredBuilds
        .filter((build) => build.project === project.name)
        .sort((buildA, buildB) => buildB.createdAt.localeCompare(buildA.createdAt))
        .reduce<Set<string>>((acc, build) => acc.add(build.configVersion), new Set());
    }
    return [...verSet];
  }, [filteredBuilds, project]);

  const isValidDeploy = useMemo(() => {
    return Boolean(customerId && artifactVersion && targetGroupName);
  }, [artifactVersion, customerId, targetGroupName]);

  return (
    <ControllerModal onClose={onClose} open={open} title={`Deploy a ${project.name} build`}>
      <DialogContent>
        <Stack py={2} direction='column' alignItems='flex-start' spacing={2}>
          <Typography>
            Based on the project <strong>{project.name}</strong>, you may only deploy to groups that share the
            same device family of <strong>{project.deviceFamily}</strong>
          </Typography>
          {controllerBuildEnabled && (
            <Button color='primary' size='medium' variant='outlined' onClick={handleCreateGroupModalOpen}>
              Create new group
            </Button>
          )}
        </Stack>
        <Divider />
        <Stack py={2} direction='column' spacing={2}>
          <Typography fontWeight={600}>Deploy existing {DEPLOY_TYPE_MAP.get(ArtifactType.Config)}</Typography>
          <Stack direction='column' spacing={3}>
            <ControllerAutocomplete<string>
              AutocompleteProps={{
                id: 'target-group',
                value: targetGroupName,
                options: filteredGroups?.map((group) => group.name) || [],
                onChange: handleTargetGroupChange,
              }}
              label='Target group'
              placeholder={filteredGroups?.length ? 'Select a group' : 'No compatible groups'}
              autoFocus
              required
              fullWidth
            />
            <ControllerSelect
              label='Version'
              placeholder={versionOptions.length ? 'Select a build version' : 'No completed builds'}
              autoFocus
              required
              fullWidth
              value={artifactVersion || ''}
              onChange={handleArtifactVersionChange}
              disabled={!versionOptions.length}
            >
              {versionOptions.map((ver) => (
                <ControllerMenuItem key={ver} value={ver}>
                  {ver}
                </ControllerMenuItem>
              ))}
            </ControllerSelect>
            {!deployBusy && deployError && (
              <Alert severity='error' variant='outlined' sx={{ width: '100%' }}>
                There was a problem issuing the deployment
              </Alert>
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color='secondary' onClick={onClose} size='medium' variant='text'>
          Cancel
        </Button>
        <Button
          color='primary'
          size='medium'
          variant='contained'
          onClick={handleDeployArtifact}
          disabled={!isValidDeploy}
        >
          Deploy {DEPLOY_TYPE_MAP.get(ArtifactType.Config)}
        </Button>
      </DialogActions>
      <CreateGroupModal
        open={showCreateGroupModal}
        onClose={handleCreateGroupModalClose}
        onSuccess={handleCreateGroupSuccess}
        onError={onError}
      />
    </ControllerModal>
  );
}
